/**
 * t222u: the slim ⋯ More button at the right end of the phone tab bar (full bar height as its tap target, about 30px
 * wide) and its bottom-sheet menu: phone places that aren't tabs (People (t231u), Buildings, My issues, Announcements, Calendar,
 * Approvals for managers, My runs for drivers, Help), only the ones this person can open.
 */
import { useEffect, useMemo, useState } from 'react';
import clsx from 'clsx';
import { useLocation, useNavigate } from 'react-router-dom';
import { Building2, CalendarDays, ChevronRight, Plane, Inbox, LifeBuoy, Megaphone, MoreHorizontal, Navigation, Route, Siren, Truck, Users, Wrench, DoorOpen, GraduationCap, LayoutDashboard, MapPinned, ScrollText, type LucideIcon, ScanLine } from 'lucide-react';
import { isMorePath, moreItems, type MoreItemId } from '@tradeoutlet/shared';
import { holdsAnywhere, useNavAccess } from '@/lib/navAccess';
import { usePermissions } from '@/lib/permissions';
import { BottomSheet } from './BottomSheet';
import { useMobileTabs } from './useMobileTabs';

const ICON: Record<MoreItemId, LucideIcon> = { dashboard: LayoutDashboard, people: Users, buildings: Building2, issues: Siren, equipment: Wrench, scan: ScanLine, visitors: DoorOpen, training: GraduationCap, policies: ScrollText, announcements: Megaphone, calendar: CalendarDays, timeoff: Plane, approvals: Inbox, 'fleet-dashboard': LayoutDashboard, journeys: Route, live: MapPinned, vehicles: Truck, runs: Navigation, help: LifeBuoy };

export function useMoreItems() {
  const { role, slots } = useMobileTabs();
  const mine = usePermissions().data;
  const { can } = useNavAccess();
  return useMemo(() => moreItems(role, p => holdsAnywhere(mine, p), slots, mine?.modules_off).filter(i => can(i.to) || (i.id === 'dashboard' && can('/sales'))), [role, slots, mine, can]); // t554u: Store sales lives on the phone Dashboard
}

/** The ⋯ tab-bar button and its menu (one element so the shell only adds one line). */
/** `lit`: the tab bar's say on whether ⋯ is the lit tab (B3: an event opened from Home keeps Home lit, not ⋯). */
export function MoreTab({ lit }: { lit?: boolean } = {}) {
  const [open, setOpen] = useState(false);
  const items = useMoreItems();
  const { pathname, search } = useLocation();
  const nav = useNavigate();
  useEffect(() => { setOpen(false); }, [pathname, search]);
  const on = !open && (lit ?? isMorePath(pathname, items));
  return (
    <>
      <button type="button" onClick={() => setOpen(true)} aria-label="More" aria-haspopup="dialog" aria-expanded={open} aria-current={on ? 'page' : undefined} data-testid="tab-more"
        className={clsx('flex min-h-[56px] w-[30px] shrink-0 items-center justify-center', on || open ? 'text-white' : 'text-white/70')}>
        <span className={clsx('flex h-7 w-7 items-center justify-center rounded-full', (on || open) && 'bg-accent text-on-accent')}><MoreHorizontal className="h-5 w-5" aria-hidden /></span>
      </button>
      <BottomSheet open={open} onClose={() => setOpen(false)} title="More" testId="more-sheet">
        <ul>
          {items.map(i => {
            const Icon = ICON[i.id];
            const here = pathname === i.to || pathname.startsWith(`${i.to}/`);
            return (
              <li key={i.id}>
                <button type="button" onClick={() => { setOpen(false); nav(i.to); }} data-testid={`more-${i.id}`} aria-current={here ? 'page' : undefined}
                  className="flex min-h-[56px] w-full items-center gap-3 px-4 text-left text-base active:bg-slate-100 dark:active:bg-slate-800">
                  <span className={clsx('flex h-9 w-9 items-center justify-center rounded-full', here ? 'bg-accent text-on-accent ring-1 ring-black' : 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-200')}><Icon className="h-5 w-5" aria-hidden /></span>
                  <span className="flex-1">{i.label}</span><ChevronRight className="h-5 w-5 text-slate-400" aria-hidden />
                </button>
              </li>
            );
          })}
        </ul>
      </BottomSheet>
    </>
  );
}
